import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { useAuth, homePathFor } from "@/lib/auth";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
import { ModuleSwitcher } from "@/components/ModuleSwitcher";
import { IdleManager } from "@/components/IdleManager";
import { TrakLogo } from "@/components/TrakLogo";
import { Breadcrumbs, BreadcrumbProvider } from "@/components/Breadcrumbs";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldCheck, ChartLine, Warning, FileText, GearSix, SignOut, Crosshair, TrendUp, ArrowLeft, Bell, ArrowsLeftRight, SlidersHorizontal, BookBookmark, Users } from "@phosphor-icons/react";

const isLead = (u) => u && (u.role === "soc_lead" || u.role === "admin");
const isAdmin = (u) => u && u.role === "admin";

const NOTIF_LABEL = { mention: "mentioned you", assigned: "assigned a case to you", sla_breach: "SLA breach on your case", released: "released a case to the queue", unassigned: "case still untaken", escalated: "escalated a case to Tier 2" };

function NotifyPrefsDialog() {
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/soc/notifications/prefs").then(r => setPrefs(r.data)).catch(() => {});
  useEffect(() => { if (open) load(); }, [open]);
  const save = async () => {
    setSaving(true);
    try { await api.put("/soc/notifications/prefs", prefs); toast.success("Notification preferences saved"); setOpen(false); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const Row = ({ k, label, hint }) => (
    <label className="flex items-center justify-between gap-3 text-sm border border-border rounded-sm px-3 py-2 cursor-pointer">
      <span><span className="font-medium">{label}</span>{hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}</span>
      <Switch data-testid={`soc-pref-${k}`} checked={!!prefs?.[k]} onCheckedChange={v => setPrefs({ ...prefs, [k]: v })} />
    </label>
  );
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button data-testid="soc-notif-prefs-btn" onClick={() => setOpen(true)}
        className="text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-foreground transition-colors" title="Notification preferences">
        <SlidersHorizontal size={18} />
      </button>
      <DialogContent className="max-w-md" data-testid="soc-notif-prefs-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><SlidersHorizontal size={18} /> Notification preferences</DialogTitle>
        <DialogDescription>Choose how you want to be notified about SOC activity that involves you.</DialogDescription></DialogHeader>
        {prefs ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Delivery</div>
              <Row k="email_realtime" label="Real-time email" hint="Email me immediately when an event I care about happens" />
              <Row k="email_digest" label="Daily email digest" hint="One summary email per day of my notifications" />
              <p className="text-[11px] text-muted-foreground">In-app notifications (the bell) are always on.</p>
            </div>
            <div className="space-y-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Notify me about</div>
              <Row k="on_mention" label="@mentions" />
              <Row k="on_assigned" label="Cases assigned to me" />
              <Row k="on_sla_breach" label="SLA breach on my cases" />
            </div>
          </div>
        ) : <div className="text-sm text-muted-foreground py-4">Loading…</div>}
        <DialogFooter>
          <Button data-testid="soc-pref-save" onClick={save} disabled={saving || !prefs} className="rounded-sm font-mono uppercase text-xs">{saving ? "Saving…" : "Save preferences"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SocNotificationBell() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const load = useCallback(() => {
    api.get("/soc/notifications").then(r => {
      setItems(r.data);
      setCount(r.data.filter(n => !n.read).length);
    }).catch(() => {});
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(() => {
      api.get("/soc/notifications/unread-count").then(r => setCount(r.data.count || 0)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, [load]);
  const openItem = async (n) => {
    try { await api.post(`/soc/notifications/${n.id}/read`); } catch { /* best effort */ }
    load();
    if (n.case_id) nav(`/soc/cases/${n.case_id}`);
  };
  const markAll = async () => { try { await api.post("/soc/notifications/read-all"); } catch { /* */ } load(); };
  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="soc-notif-bell" className="relative text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-foreground transition-colors" title="Notifications">
          <Bell size={18} />
          {count > 0 && <span data-testid="soc-notif-count" className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 grid place-items-center text-[9px] font-mono rounded-full bg-rag-red text-background">{count > 9 ? "9+" : count}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="soc-notif-menu">
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <span className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Notifications</span>
          <button data-testid="soc-notif-readall" onClick={markAll} className="text-[10px] font-mono uppercase text-primary hover:underline">Mark all read</button>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 && <div className="px-3 py-6 text-center text-xs text-muted-foreground">No notifications.</div>}
          {items.map(n => (
            <button key={n.id} data-testid={`soc-notif-${n.id}`} onClick={() => openItem(n)}
              className={cn("w-full text-left px-3 py-2 border-b border-border/60 hover:bg-accent/40 transition-colors", !n.read && "bg-primary/5")}>
              <div className="text-xs">
                <span className="font-medium">{n.actor_name || "System"}</span> {NOTIF_LABEL[n.type] || "notified you"}
                {n.case_number && <span className="font-mono text-primary"> · {n.case_number}</span>}
                {n.task_title && <span className="text-muted-foreground"> · task</span>}
              </div>
              <div className="text-[11px] text-muted-foreground truncate mt-0.5">{n.text}</div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function SocLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const viewer = user?.role === "soc_viewer";
  useEffect(() => {
    if (viewer) localStorage.setItem("helix_wall", "1"); else localStorage.removeItem("helix_wall");
  }, [viewer]);

  const items = [
    { to: "/soc", label: "SOC Dashboard", icon: ChartLine, exact: true, testid: "soc-nav-dashboard", show: true },
    { to: "/soc/cases", label: "Cases", icon: Warning, testid: "soc-nav-cases", show: !viewer },
    { to: "/soc/iocs", label: "IOC Search", icon: Crosshair, testid: "soc-nav-iocs", show: !viewer },
    { to: "/soc/library", label: "Intel Library", icon: BookBookmark, testid: "soc-nav-library", show: !viewer },
    { to: "/soc/trends", label: "Trends", icon: TrendUp, testid: "soc-nav-trends", show: !viewer },
    { to: "/soc/handover", label: "Handover", icon: ArrowsLeftRight, testid: "soc-nav-handover", show: !viewer },
    { to: "/soc/team-load", label: "Team Load", icon: Users, testid: "soc-nav-team-load", show: isLead(user) },
    { to: "/soc/reports", label: "Reports", icon: FileText, testid: "soc-nav-reports", show: !viewer },
    { to: "/soc/settings", label: "Settings", icon: GearSix, testid: "soc-nav-settings", show: isLead(user) },
  ].filter(i => i.show);

  const linkClass = ({ isActive }) => cn(
    "flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-2 border-transparent",
    "text-sidebar-muted hover:text-white hover:bg-white/5",
    "dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40",
    isActive && "text-white bg-white/10 border-white dark:text-foreground dark:bg-primary-tint dark:border-primary"
  );

  return (
    <div className="min-h-screen flex bg-background text-foreground" data-testid={viewer ? "soc-videowall" : undefined}>
      {!viewer && <IdleManager />}
      <aside className="w-60 shrink-0 border-r flex flex-col bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
        <div className="h-14 px-5 border-b border-sidebar-border dark:border-border flex items-center gap-2">
          <button
            data-testid="soc-home-logo"
            onClick={() => nav(homePathFor(user))}
            title="Back to start"
            className="flex items-center gap-2 hover:opacity-80 transition-opacity cursor-pointer"
          >
            <div className="h-7 w-7 grid place-items-center bg-white text-sidebar dark:bg-primary dark:text-primary-foreground rounded-sm">
              <TrakLogo size={18} />
            </div>
            <div className="font-display font-bold tracking-tight">TRAK</div>
          </button>
          <span className="ml-auto text-[10px] font-mono text-rag-red uppercase flex items-center gap-1"><ShieldCheck size={12} weight="fill" /> SOC</span>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {!viewer && <button
            data-testid="soc-nav-back"
            onClick={() => nav(-1)}
            className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-sidebar-muted hover:text-white hover:bg-white/5 dark:text-muted-foreground dark:hover:text-foreground dark:hover:bg-accent/40 transition-colors border-l-2 border-transparent"
          >
            <ArrowLeft size={16} weight="regular" /> Back
          </button>}
          {items.map(({ to, label, icon: Icon, exact, testid }) => (
            <NavLink key={to} to={to} end={exact} data-testid={testid} className={linkClass}>
              <Icon size={16} weight="regular" /> {label}
            </NavLink>
          ))}
          {viewer && (
            <div className="mx-5 mt-4 p-3 border border-sidebar-border dark:border-border rounded-sm text-[11px] font-mono text-sidebar-muted dark:text-muted-foreground" data-testid="soc-videowall-note">
              <div className="text-white dark:text-foreground uppercase tracking-widest mb-1">Videowall mode</div>
              Read-only · refreshes every 30s · session lasts 24h, then signs in again automatically.
            </div>
          )}
        </nav>
        <div className="border-t p-4 border-sidebar-border dark:border-border">
          {isAdmin(user) && (
            <button data-testid="soc-back-to-itsm" onClick={() => nav("/")}
              className="w-full mb-3 text-[11px] font-mono uppercase tracking-widest text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-primary flex items-center gap-2">
              <ChartLine size={13} /> Back to ITSM
            </button>
          )}
          <div className="text-xs font-mono text-sidebar-muted dark:text-muted-foreground">SIGNED IN AS</div>
          <div className="text-sm mt-1">{user?.name}</div>
          <div className="text-[11px] font-mono text-sidebar-muted dark:text-muted-foreground">{user?.email}</div>
          <div className="mt-2 flex items-center justify-between">
            <span className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-sidebar-border dark:border-border">{user?.role?.replace("_", " ")}</span>
            <button data-testid="soc-logout-button" onClick={async () => { await logout(); nav("/login", { replace: true }); }}
              className="text-sidebar-muted hover:text-white dark:text-muted-foreground dark:hover:text-primary" title="Sign out"><SignOut size={16} /></button>
          </div>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <BreadcrumbProvider>
          <header className="h-14 border-b flex items-center px-6 gap-4 bg-sidebar text-sidebar-foreground border-sidebar-border dark:bg-background dark:text-foreground dark:border-border">
            <Breadcrumbs />
            <div className="ml-auto flex items-center gap-4">
              {!viewer && <ModuleSwitcher />}
              {!viewer && <NotifyPrefsDialog />}
              {!viewer && <SocNotificationBell />}
              <ThemeToggle />
            </div>
          </header>
          <main className="flex-1 overflow-y-auto"><Outlet /></main>
        </BreadcrumbProvider>
      </div>
    </div>
  );
}
